import get from 'lodash-es/get.js';
import { createSelector } from 'reselect';
import * as firestoreRedux from '@dreamworld/firestore-redux';
import * as feature from '../feature/index.js';
import * as app from '../app/index.js';

export const profilesQueryId = (accountId) => `ai-agent-account-profiles-${accountId}`;
export const tokensQueryId = (userId) => `ai-team-member-tokens-${userId}`;
export const workersQueryId = (userId) => `ai-team-member-workers-${userId}`;

/**
 * @returns {Array|undefined} the query's docs once its first snapshot arrived; `undefined` while pending or failed.
 */
const loadedDocs = (state, queryId, collection) => {
  const status = firestoreRedux.selectors.queryStatus(state, queryId);
  return status === 'LIVE' || status === 'CLOSED' ? firestoreRedux.selectors.docsByQueryResult(state, queryId, collection) : undefined;
};

const agentOf = createSelector(
  (profiles, userId) => profiles && profiles.find((profile) => profile.agentId === userId),
  (profiles) => profiles === undefined,
  (profile, isPending) => (isPending ? undefined : { profile })
);

/**
 * @returns {Object|undefined} `{ profile }`, the agent's `ai-agent-account-profiles` doc (`instructions`), once `load`'s
 * query answered. Its name and picture are on its `users` doc (`user.selectors.attrs`).
 */
export const agent = ({ state, accountId, userId }) =>
  agentOf(loadedDocs(state, profilesQueryId(accountId), 'ai-agent-account-profiles'), userId);

const newestFirst = createSelector((tokens) => tokens, (tokens) => tokens && [...tokens].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));

/**
 * @returns {Array|undefined} the agent's `access-tokens` docs, newest first; `undefined` until loaded. Token values are
 * never in Firestore.
 */
export const tokens = ({ state, userId }) => newestFirst(loadedDocs(state, tokensQueryId(userId), 'access-tokens'));

/**
 * @returns {Array|undefined} the agent's `ai-agent-workers` docs; `undefined` until loaded, `[]` when none has connected.
 */
export const workers = ({ state, userId }) => loadedDocs(state, workersQueryId(userId), 'ai-agent-workers');

/**
 * @returns {String|undefined} `IN_PROGRESS` | `SUCCESS` | `FAILED` for a request key such as `issueToken:usr_<id>`.
 */
export const requestStatus = ({ state, key }) => get(state, `ai-team-member.requests.${key}`);

/**
 * Read from the board page's collection-group query on `ai-agent-board-profiles` by `boardId`, which every board reader can read.
 * @returns {String} the agent's instructions for the board (HTML), `''` when none.
 */
export const boardInstructions = ({ state, boardId, userId }) => {
  const profiles = Object.values(firestoreRedux.selectors.collection(state, 'ai-agent-board-profiles') || {});
  const profile = profiles.find((doc) => doc && doc.boardId === boardId && doc.agentId === userId);
  return (profile && profile.instructions) || '';
};

/** @returns {Boolean} feature-flag gate; wraps `feature.selectors.isAvailable`. */
export const isAvailable = (state, accountId) => feature.selectors.isAvailable(state, 'ai-team-member', accountId);

/**
 * The one gate for showing a user as an AI Team Member: its ring, states and labels.
 * @returns {Boolean} `true` when the user is an agent and the feature is available for the account it belongs to.
 */
export const isAgent = (state, user) => !!user?.aiAgent && isAvailable(state, user.agentAccountId);

/**
 * @returns {String} the MCP server URL from the agent's create or detail response; the API base URL + `/ai/mcp` when the
 * response has none.
 */
export const mcpUrl = ({ state, userId }) =>
  get(state, `ai-team-member.mcpUrls.${userId}`) || `${app.selectors.apiBaseUrl(state)}/ai/mcp`;
